import React, { useEffect, useState, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { 
  getFinancialLaunches, 
  createFinancialLaunch, 
  createDirectLaunch,
  getFinancialLaunch, 
  uploadReceipt,
  uploadDirectReceipt,
  confirmPayment,
  getProjects, 
  getProviders, 
  getCostCenters,
  getProjectEmpreitas
} from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { useSharedFile } from '../contexts/SharedFileContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { Plus, Receipt, Upload, Copy, CheckCircle2, Clock, FileText, Trash2, Share2 } from 'lucide-react';
import BulkImport from '../components/BulkImport';

const FinancialLaunches = () => {
  const { user } = useAuth();
  const { sharedFile, clearFile } = useSharedFile();
  const [searchParams, setSearchParams] = useSearchParams();
  const isShareMode = searchParams.get('share') === '1' && !!sharedFile;
  const isAdmin = user?.role === 'administrator';
  const [launches, setLaunches] = useState([]);
  const [projects, setProjects] = useState([]);
  const [providers, setProviders] = useState([]);
  const [costCenters, setCostCenters] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedLaunch, setSelectedLaunch] = useState(null);
  const [isDetailDialogOpen, setIsDetailDialogOpen] = useState(false);
  const fileInputRef = useRef(null);
  const directFileInputRef = useRef(null);
  
  // Launch type: 'client_material' (fornecedor material), 'client_empreita' (cobrar cliente na empreita), 'provider_empreita' (pagar prestador), 'direct' (rateio interno)
  const [launchType, setLaunchType] = useState('client_material');

  // Form data for client_material (legacy "client" form)
  const [formData, setFormData] = useState({
    project_id: '',
    provider_id: '',
    service_code: '',
    service_description: '',
    launch_date: new Date().toISOString().split('T')[0],
    amount: '',
    payment_info: ''
  });

  // Form data for empreita flow (both sides)
  const [empreitaFormData, setEmpreitaFormData] = useState({
    side: 'client', // 'client' (cobrar cliente) or 'provider' (pagar prestador)
    project_id: '',
    empreita_id: '',
    provider_id: '',
    launch_date: new Date().toISOString().split('T')[0],
    amount: '',
    service_description: ''
  });
  const [empreitasForProject, setEmpreitasForProject] = useState([]);

  // Form data for direct launch
  const [directFormData, setDirectFormData] = useState({
    provider_id: '',
    service_code: '',
    service_description: '',
    launch_date: new Date().toISOString().split('T')[0],
    total_amount: '',
    payment_info: '',
    selectedProjects: [], // [{project_id, amount}]
    receiptFile: null
  });

  // Filtered data based on selections
  const [availableProviders, setAvailableProviders] = useState([]);
  const [availableServices, setAvailableServices] = useState([]);
  
  // For direct launch - services derived from allServicesMemo directly (no state needed)

  useEffect(() => {
    fetchData();
  }, []);

  // Filter providers when project changes (for client launch)
  useEffect(() => {
    if (formData.project_id && isAdmin) {
      const selectedProject = projects.find(p => p.id === formData.project_id);
      if (selectedProject?.assignments) {
        const providerIds = [...new Set(selectedProject.assignments.map(a => a.provider_id))];
        const filteredProviders = providers.filter(p => providerIds.includes(p.id));
        setAvailableProviders(filteredProviders);
      } else {
        setAvailableProviders([]);
      }
      setFormData(prev => ({
        ...prev,
        provider_id: '',
        service_code: '',
        service_description: '',
        payment_info: ''
      }));
      setAvailableServices([]);
    }
  }, [formData.project_id, projects, providers, isAdmin]);

  // Filter services when provider changes (for client launch)
  useEffect(() => {
    if (formData.project_id && formData.provider_id && isAdmin) {
      const selectedProject = projects.find(p => p.id === formData.project_id);
      if (selectedProject?.assignments) {
        const providerAssignment = selectedProject.assignments.find(a => a.provider_id === formData.provider_id);
        if (providerAssignment?.services) {
          setAvailableServices(providerAssignment.services);
        } else {
          setAvailableServices([]);
        }
      }
    }
  }, [formData.provider_id, formData.project_id, projects, isAdmin]);

  // Direct launch: warning about provider without empreita in project (feature paused — was causing Radix loop)
  const providerNewInProjects = [];

  // Memoized deduped list of services across all cost centers (stable reference)
  const allServicesMemo = React.useMemo(() => {
    const map = new Map();
    for (const cc of costCenters) {
      for (const s of (cc.services || [])) {
        if (s.service_code && !map.has(s.service_code)) map.set(s.service_code, s);
      }
    }
    return Array.from(map.values());
  }, [costCenters]);

  const fetchData = async () => {
    try {
      const [launchesRes, projectsRes, providersRes, costCentersRes] = await Promise.all([
        getFinancialLaunches(),
        getProjects(),
        isAdmin ? getProviders() : Promise.resolve({ data: [] }),
        isAdmin ? getCostCenters() : Promise.resolve({ data: [] })
      ]);
      setLaunches(launchesRes.data);
      setProjects(projectsRes.data);
      if (isAdmin) {
        setProviders(providersRes.data);
        setCostCenters(costCentersRes.data);
      }
    } catch (error) {
      toast.error('Erro ao carregar dados');
    }
  };

  const resetForm = () => {
    setFormData({
      project_id: '',
      provider_id: '',
      service_code: '',
      service_description: '',
      launch_date: new Date().toISOString().split('T')[0],
      amount: '',
      payment_info: ''
    });
    setDirectFormData({
      provider_id: '',
      service_code: '',
      service_description: '',
      launch_date: new Date().toISOString().split('T')[0],
      total_amount: '',
      payment_info: '',
      selectedProjects: [],
      receiptFile: null
    });
    setAvailableProviders([]);
    setAvailableServices([]);
    setLaunchType('client_material');
    setEmpreitaFormData({
      side: 'client',
      project_id: '',
      empreita_id: '',
      provider_id: '',
      launch_date: new Date().toISOString().split('T')[0],
      amount: '',
      service_description: ''
    });
    setEmpreitasForProject([]);
  };

  // Fetch empreitas when project changes (empreita flow)
  useEffect(() => {
    const fetch = async () => {
      if (!empreitaFormData.project_id) {
        setEmpreitasForProject([]);
        return;
      }
      try {
        const res = await getProjectEmpreitas(empreitaFormData.project_id);
        setEmpreitasForProject(res.data);
      } catch (e) {
        setEmpreitasForProject([]);
      }
    };
    fetch();
  }, [empreitaFormData.project_id]);

  const handleProjectChange = (projectId) => {
    setFormData({
      ...formData,
      project_id: projectId,
      provider_id: '',
      service_code: '',
      service_description: '',
      payment_info: ''
    });
  };

  const handleProviderChange = (providerId) => {
    const provider = providers.find(p => p.id === providerId);
    setFormData({
      ...formData,
      provider_id: providerId,
      service_code: '',
      service_description: '',
      payment_info: provider?.payment_info || ''
    });
  };

  const handleServiceChange = (serviceCode) => {
    const service = availableServices.find(s => s.service_code === serviceCode);
    if (service) {
      setFormData({
        ...formData,
        service_code: serviceCode,
        service_description: service.service_description
      });
    }
  };

  // Direct launch handlers
  const handleDirectProviderChange = (providerId) => {
    const provider = providers.find(p => p.id === providerId);
    setDirectFormData({
      ...directFormData,
      provider_id: providerId,
      service_code: '',
      service_description: '',
      payment_info: provider?.payment_info || ''
    });
  };

  const handleDirectServiceChange = (serviceCode) => {
    const service = allServicesMemo.find(s => s.service_code === serviceCode);
    if (service) {
      setDirectFormData({
        ...directFormData,
        service_code: serviceCode,
        service_description: service.service_description
      });
    }
  };

  const toggleProjectForDirect = (projectId) => {
    const isSelected = directFormData.selectedProjects.some(p => p.project_id === projectId);
    if (isSelected) {
      setDirectFormData({
        ...directFormData,
        selectedProjects: directFormData.selectedProjects.filter(p => p.project_id !== projectId)
      });
    } else {
      setDirectFormData({
        ...directFormData,
        selectedProjects: [...directFormData.selectedProjects, { project_id: projectId, amount: '' }]
      });
    }
  };

  const updateProjectAmount = (projectId, amount) => {
    setDirectFormData({
      ...directFormData,
      selectedProjects: directFormData.selectedProjects.map(p =>
        p.project_id === projectId ? { ...p, amount: amount } : p
      )
    });
  };

  const calculateRateioTotal = () => {
    return Math.round(directFormData.selectedProjects.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0) * 100) / 100;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (launchType === 'client_material') {
      // Material launch (current behavior)
      try {
        await createFinancialLaunch({
          ...formData,
          amount: parseFloat(formData.amount),
          launch_type: 'client_material'
        });
        toast.success('Lançamento criado com sucesso!');
        setIsDialogOpen(false);
        resetForm();
        fetchData();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Erro ao criar lançamento');
      }
    } else if (launchType === 'empreita') {
      // Empreita flow — side decides client_empreita vs provider_empreita
      if (!empreitaFormData.empreita_id || !empreitaFormData.amount) {
        toast.error('Selecione a empreita e informe o valor');
        return;
      }
      const finalType = empreitaFormData.side === 'provider' ? 'provider_empreita' : 'client_empreita';
      if (finalType === 'provider_empreita' && !empreitaFormData.provider_id) {
        toast.error('Selecione o prestador');
        return;
      }
      try {
        const payload = {
          launch_type: finalType,
          empreita_id: empreitaFormData.empreita_id,
          provider_id: finalType === 'provider_empreita' ? empreitaFormData.provider_id : null,
          launch_date: empreitaFormData.launch_date,
          amount: parseFloat(empreitaFormData.amount),
          service_description: empreitaFormData.service_description || null,
          payment_info: null,
          project_id: empreitaFormData.project_id || null
        };
        await createFinancialLaunch(payload);
        toast.success(finalType === 'provider_empreita' ? 'Lançamento para prestador criado!' : 'Cobrança de empreita criada!');
        setIsDialogOpen(false);
        resetForm();
        fetchData();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Erro ao criar lançamento de empreita');
      }
    } else {
      // Direct launch (unchanged)
      const totalAmount = parseFloat(directFormData.total_amount);
      const rateioTotal = calculateRateioTotal();
      
      if (Math.abs(totalAmount - rateioTotal) > 0.01) {
        toast.error(`Soma do rateio (R$ ${rateioTotal.toFixed(2)}) não corresponde ao valor total (R$ ${totalAmount.toFixed(2)})`);
        return;
      }
      
      if (!directFormData.receiptFile) {
        toast.error('É necessário anexar o comprovante para Lançamento Direto');
        return;
      }
      
      try {
        const response = await createDirectLaunch({
          provider_id: directFormData.provider_id,
          service_code: directFormData.service_code,
          service_description: directFormData.service_description,
          launch_date: directFormData.launch_date,
          total_amount: totalAmount,
          payment_info: directFormData.payment_info,
          projects: directFormData.selectedProjects.map(p => ({
            project_id: p.project_id,
            amount: parseFloat(p.amount)
          }))
        });
        
        // Upload receipt for the group
        if (response.data.length > 0 && response.data[0].direct_launch_group_id) {
          await uploadDirectReceipt(response.data[0].direct_launch_group_id, directFormData.receiptFile);
        }
        
        toast.success('Lançamento Direto criado com sucesso!');
        setIsDialogOpen(false);
        resetForm();
        fetchData();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Erro ao criar lançamento direto');
      }
    }
  };

  const handleOpenDetails = async (launchId) => {
    try {
      const response = await getFinancialLaunch(launchId);
      setSelectedLaunch(response.data);
      setIsDetailDialogOpen(true);
    } catch (error) {
      toast.error('Erro ao carregar detalhes do lançamento');
    }
  };

  const handleUploadReceipt = async (e) => {
    const file = e.target.files[0];
    if (!file || !selectedLaunch) return;

    try {
      await uploadReceipt(selectedLaunch.id, file);
      toast.success('Comprovante enviado com sucesso!');
      const response = await getFinancialLaunch(selectedLaunch.id);
      setSelectedLaunch(response.data);
      fetchData();
      // Clear share mode after successful upload
      if (isShareMode) { clearFile(); setSearchParams({}); }
    } catch (error) {
      toast.error('Erro ao enviar comprovante');
    }
  };

  const handleSharedFileUpload = async (launch) => {
    if (!sharedFile?.file) return;
    try {
      await uploadReceipt(launch.id, sharedFile.file);
      toast.success('Comprovante enviado com sucesso!');
      clearFile();
      setSearchParams({});
      fetchData();
    } catch (error) {
      toast.error('Erro ao enviar comprovante');
    }
  };

  const handleConfirmPayment = async () => {
    if (!selectedLaunch) return;
    
    try {
      await confirmPayment(selectedLaunch.id);
      toast.success('Pagamento confirmado com sucesso!');
      // Refresh the selected launch
      const response = await getFinancialLaunch(selectedLaunch.id);
      setSelectedLaunch(response.data);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao confirmar pagamento');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copiado para a área de transferência!');
  };

  const openReceipt = (receiptUrl) => {
    if (receiptUrl) {
      // Handle both old (/uploads/...) and new (/api/uploads/...) paths
      const url = receiptUrl.startsWith('/api/') ? receiptUrl : `/api${receiptUrl}`;
      const fullUrl = `${process.env.REACT_APP_BACKEND_URL}${url}`;
      window.open(fullUrl, '_blank');
    }
  };

  const getStatusBadge = (status) => {
    if (status === 'paid') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-sm text-xs font-medium text-emerald-600 bg-emerald-50">
          <CheckCircle2 className="h-3 w-3" />
          Pago
        </span>
      );
    }
    if (status === 'submitted_receipt') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-sm text-xs font-medium text-blue-600 bg-blue-50">
          <Upload className="h-3 w-3" />
          Comprovante Enviado
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-sm text-xs font-medium text-amber-600 bg-amber-50">
        <Clock className="h-3 w-3" />
        Pendente
      </span>
    );
  };

  const getLaunchTypeBadge = (type) => {
    if (type === 'direct') {
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded-sm text-xs font-medium text-purple-600 bg-purple-50">
          Direto
        </span>
      );
    }
    if (type === 'client_empreita') {
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded-sm text-xs font-medium text-blue-600 bg-blue-50">
          Empreita · Cliente
        </span>
      );
    }
    if (type === 'provider_empreita') {
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded-sm text-xs font-medium text-amber-700 bg-amber-50">
          Empreita · Prestador
        </span>
      );
    }
    return null;
  };

  // Filter launches for display
  const clientLaunches = launches.filter(l => ['client', 'client_material', 'client_empreita'].includes(l.launch_type));
  const directLaunches = launches.filter(l => l.launch_type === 'direct');
  const providerEmpreitaLaunches = launches.filter(l => l.launch_type === 'provider_empreita');
  // In share mode for client: show only pending launches without receipt
  const pendingForShare = isShareMode && !isAdmin
    ? clientLaunches.filter(l => l.status === 'pending' && !l.receipt_file)
    : [];

  return (
    <div className="space-y-6" data-testid="financial-launches-page">
      {/* Share mode banner for clients */}
      {isShareMode && !isAdmin && (
        <div className="flex items-center gap-3 p-4 bg-blue-50 border border-blue-200 rounded-sm" data-testid="share-mode-banner-client">
          <Share2 className="h-5 w-5 text-blue-600 shrink-0" />
          <div className="flex-1">
            <p className="text-sm font-medium text-blue-900">Arquivo recebido: {sharedFile?.fileName}</p>
            <p className="text-xs text-blue-700">Selecione um lançamento pendente para anexar como comprovante</p>
          </div>
          <Button variant="ghost" size="sm" onClick={() => { clearFile(); setSearchParams({}); }}
            className="text-blue-600 hover:text-blue-800">Cancelar</Button>
        </div>
      )}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-secondary font-bold uppercase tracking-tight text-slate-900">
            {isAdmin ? 'Lançamentos Financeiros' : 'Meus Pagamentos'}
          </h1>
          <p className="text-slate-600 font-primary mt-1">
            {isAdmin ? 'Gerencie todos os lançamentos financeiros' : 'Visualize e pague seus lançamentos'}
          </p>
        </div>
        {isAdmin && (
          <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
            <BulkImport entity="launches" label="Lançamentos" onDone={fetchData} buttonClassName="rounded-sm w-full sm:w-auto" />
            <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
            <Button 
              onClick={() => setIsDialogOpen(true)}
              className="bg-slate-900 text-white hover:bg-slate-800 rounded-sm w-full sm:w-auto"
              data-testid="add-launch-button"
            >
              <Plus className="h-4 w-4 mr-2" />
              Novo Lançamento
            </Button>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-secondary uppercase tracking-tight">
                  Novo Lançamento
                </DialogTitle>
              </DialogHeader>
              
              {/* Launch Type Selector */}
              <div className="mb-4">
                <Label className="font-secondary uppercase text-xs tracking-wider text-slate-500 mb-2 block">
                  Tipo de Destinatário
                </Label>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  <Button
                    type="button"
                    variant={launchType === 'client_material' ? 'default' : 'outline'}
                    onClick={() => setLaunchType('client_material')}
                    className={launchType === 'client_material' ? 'bg-slate-900' : ''}
                    data-testid="launch-type-material"
                  >
                    Fornecedor (Material)
                  </Button>
                  <Button
                    type="button"
                    variant={launchType === 'empreita' ? 'default' : 'outline'}
                    onClick={() => setLaunchType('empreita')}
                    className={launchType === 'empreita' ? 'bg-emerald-700 hover:bg-emerald-800' : ''}
                    data-testid="launch-type-empreita"
                  >
                    Prestador (Empreita)
                  </Button>
                  <Button
                    type="button"
                    variant={launchType === 'direct' ? 'default' : 'outline'}
                    onClick={() => setLaunchType('direct')}
                    className={launchType === 'direct' ? 'bg-purple-600 hover:bg-purple-700' : ''}
                    data-testid="launch-type-direct"
                  >
                    Lançamento Direto
                  </Button>
                </div>
              </div>

              {launchType === 'client_material' ? (
                /* CLIENT LAUNCH FORM */
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="font-secondary uppercase text-xs tracking-wider text-slate-500">
                        Projeto *
                      </Label>
                      <Select value={formData.project_id || undefined} onValueChange={handleProjectChange}>
                        <SelectTrigger className="mt-1 rounded-sm">
                          <SelectValue placeholder="Selecione" />
                        </SelectTrigger>
                        <SelectContent>
                          {projects.map(project => (
                            <SelectItem key={project.id} value={project.id}>{project.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="font-secondary uppercase text-xs tracking-wider text-slate-500">
                        Prestador/Fornecedor *
                      </Label>
                      <Select 
                        value={formData.provider_id || undefined} 
                        onValueChange={handleProviderChange}
                        disabled={!formData.project_id}
                      >
                        <SelectTrigger className="mt-1 rounded-sm">
                          <SelectValue placeholder={formData.project_id ? "Selecione" : "Selecione um projeto"} />
                        </SelectTrigger>
                        <SelectContent>
                          {availableProviders.map(provider => (
                            <SelectItem key={provider.id} value={provider.id}>{provider.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  {formData.provider_id && (
                    <div>
                      <Label className="font-secondary uppercase text-xs tracking-wider text-slate-500">
                        Serviço *
                      </Label>
                      <Select value={formData.service_code || undefined} onValueChange={handleServiceChange}>
                        <SelectTrigger className="mt-1 rounded-sm">
                          <SelectValue placeholder="Selecione" />
                        </SelectTrigger>
                        <SelectContent>
                          {availableServices.map((service, idx) => (
                            <SelectItem key={idx} value={service.service_code}>
                              {service.service_code} - {service.service_description}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="font-secondary uppercase text-xs tracking-wider text-slate-500">
                        Data *
                      </Label>
                      <Input
                        type="date"
                        value={formData.launch_date}
                        onChange={(e) => setFormData({ ...formData, launch_date: e.target.value })}
                        required
                        className="mt-1 rounded-sm"
                      />
                    </div>
                    <div>
                      <Label className="font-secondary uppercase text-xs tracking-wider text-slate-500">
                        Valor (R$) *
                      </Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={formData.amount}
                        onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                        required
                        className="mt-1 rounded-sm"
                      />
                    </div>
                  </div>

                  <div>
                    <Label className="font-secondary uppercase text-xs tracking-wider text-slate-500">
                      Informações de Pagamento *
                    </Label>
                    <Textarea
                      value={formData.payment_info}
                      onChange={(e) => setFormData({ ...formData, payment_info: e.target.value })}
                      required
                      rows={3}
                      className="mt-1 rounded-sm"
                      placeholder="Chave Pix, dados bancários..."
                    />
                  </div>

                  <div className="flex gap-2 justify-end pt-4 border-t">
                    <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                      Cancelar
                    </Button>
                    <Button type="submit" className="bg-slate-900 text-white hover:bg-slate-800">
                      Criar Lançamento
                    </Button>
                  </div>
                </form>
              ) : launchType === 'empreita' ? (
                /* EMPREITA LAUNCH FORM */
                <form onSubmit={handleSubmit} className="space-y-4" data-testid="empreita-launch-form">
                  {/* Side selector (client_empreita vs provider_empreita) */}
                  <div>
                    <Label className="font-secondary uppercase text-xs tracking-wider text-slate-500 mb-2 block">
                      Lado do Lançamento
                    </Label>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      <Button
                        type="button"
                        variant={empreitaFormData.side === 'client' ? 'default' : 'outline'}
                        onClick={() => setEmpreitaFormData({ ...empreitaFormData, side: 'client' })}
                        className={empreitaFormData.side === 'client' ? 'bg-blue-700 hover:bg-blue-800' : ''}
                        data-testid="empreita-side-client"
                      >
                        Cobrar Cliente (A Receber)
                      </Button>
                      <Button
                        type="button"
                        variant={empreitaFormData.side === 'provider' ? 'default' : 'outline'}
                        onClick={() => setEmpreitaFormData({ ...empreitaFormData, side: 'provider' })}
                        className={empreitaFormData.side === 'provider' ? 'bg-amber-700 hover:bg-amber-800' : ''}
                        data-testid="empreita-side-provider"
                      >
                        Pagar Prestador (A Pagar)
                      </Button>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label className="font-secondary uppercase text-xs tracking-wider text-slate-500">Projeto *</Label>
                      <select
                        value={empreitaFormData.project_id}
                        onChange={(e) => setEmpreitaFormData({ ...empreitaFormData, project_id: e.target.value, empreita_id: '', provider_id: '' })}
                        required
                        className="mt-1 w-full h-10 px-3 rounded-sm border border-slate-200 bg-white text-sm"
                        data-testid="empreita-project-select"
                      >
                        <option value="">Selecione</option>
                        {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                      </select>
                    </div>
                    <div>
                      <Label className="font-secondary uppercase text-xs tracking-wider text-slate-500">Empreita *</Label>
                      <select
                        value={empreitaFormData.empreita_id}
                        onChange={(e) => setEmpreitaFormData({ ...empreitaFormData, empreita_id: e.target.value, provider_id: '' })}
                        required
                        disabled={!empreitaFormData.project_id}
                        className="mt-1 w-full h-10 px-3 rounded-sm border border-slate-200 bg-white text-sm disabled:bg-slate-50"
                        data-testid="empreita-select"
                      >
                        <option value="">{empreitaFormData.project_id ? 'Selecione' : 'Escolha o projeto primeiro'}</option>
                        {empreitasForProject.map(emp => (
                          <option key={emp.id} value={emp.id}>{emp.nome} (R$ {Number(emp.valor_total_cliente).toFixed(2)})</option>
                        ))}
                      </select>
                    </div>
                    {empreitaFormData.side === 'provider' && (
                      <div className="sm:col-span-2">
                        <Label className="font-secondary uppercase text-xs tracking-wider text-slate-500">Prestador (da empreita) *</Label>
                        <select
                          value={empreitaFormData.provider_id}
                          onChange={(e) => setEmpreitaFormData({ ...empreitaFormData, provider_id: e.target.value })}
                          required
                          disabled={!empreitaFormData.empreita_id}
                          className="mt-1 w-full h-10 px-3 rounded-sm border border-slate-200 bg-white text-sm disabled:bg-slate-50"
                          data-testid="empreita-provider-payee-select"
                        >
                          <option value="">{empreitaFormData.empreita_id ? 'Selecione' : 'Escolha a empreita primeiro'}</option>
                          {(empreitasForProject.find(e => e.id === empreitaFormData.empreita_id)?.providers || []).map(link => {
                            const prov = providers.find(p => p.id === link.provider_id);
                            return (
                              <option key={link.provider_id} value={link.provider_id}>
                                {prov?.name || link.provider_id} (Custo: R$ {Number(link.valor_custo_acordado).toFixed(2)})
                              </option>
                            );
                          })}
                        </select>
                      </div>
                    )}
                    <div>
                      <Label className="font-secondary uppercase text-xs tracking-wider text-slate-500">Data *</Label>
                      <Input type="date" value={empreitaFormData.launch_date} onChange={(e) => setEmpreitaFormData({ ...empreitaFormData, launch_date: e.target.value })} required className="mt-1 rounded-sm" data-testid="empreita-launch-date-input" />
                    </div>
                    <div>
                      <Label className="font-secondary uppercase text-xs tracking-wider text-slate-500">Valor (R$) *</Label>
                      <Input type="number" step="0.01" value={empreitaFormData.amount} onChange={(e) => setEmpreitaFormData({ ...empreitaFormData, amount: e.target.value })} required className="mt-1 rounded-sm" data-testid="empreita-amount-input" />
                    </div>
                    <div className="sm:col-span-2">
                      <Label className="font-secondary uppercase text-xs tracking-wider text-slate-500">Descrição (opcional)</Label>
                      <Input value={empreitaFormData.service_description} onChange={(e) => setEmpreitaFormData({ ...empreitaFormData, service_description: e.target.value })} className="mt-1 rounded-sm" placeholder="Ex: Parcela 1, marco de execução..." />
                    </div>
                  </div>

                  {empreitaFormData.side === 'client' && (
                    <div className="p-3 bg-blue-50 border border-blue-200 rounded-sm text-xs text-blue-700" data-testid="empreita-client-hint">
                      💡 O cliente verá os dados da <strong>Construtora</strong> (CNPJ/Pix de Configurações) como favorecido. Os dados do prestador ficam ocultos.
                    </div>
                  )}
                  {empreitaFormData.side === 'provider' && (
                    <div className="p-3 bg-amber-50 border border-amber-200 rounded-sm text-xs text-amber-700" data-testid="empreita-provider-hint">
                      💡 Este lançamento será visível <strong>apenas para o prestador</strong>. O valor será validado contra o saldo acordado.
                    </div>
                  )}

                  <div className="flex gap-2 justify-end pt-4 border-t">
                    <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>Cancelar</Button>
                    <Button type="submit" className="bg-slate-900 text-white hover:bg-slate-800 rounded-sm" data-testid="submit-empreita-launch-button">
                      Criar Lançamento
                    </Button>
                  </div>
                </form>
              ) : (
                /* DIRECT LAUNCH FORM */
                <form onSubmit={handleSubmit} className="space-y-4">
                  {/* Project Selection */}
                  <div>
                    <Label className="font-secondary uppercase text-xs tracking-wider text-slate-500 mb-2 block">
                      Projetos (Rateio) *
                    </Label>
                    <div className="border rounded-sm p-3 max-h-40 overflow-y-auto space-y-2">
                      {projects.map(project => {
                        const isChecked = directFormData.selectedProjects.some(p => p.project_id === project.id);
                        return (
                          <div
                            key={project.id}
                            className={`flex items-center gap-2 p-2 rounded-sm cursor-pointer transition-all ${
                              isChecked ? 'bg-purple-50 border border-purple-200' : 'hover:bg-slate-50'
                            }`}
                            onClick={() => toggleProjectForDirect(project.id)}
                            data-testid="direct-project-checkbox"
                          >
                            <span
                              className={`inline-flex h-4 w-4 items-center justify-center rounded-sm border ${
                                isChecked ? 'bg-purple-600 border-purple-600 text-white' : 'border-slate-300 bg-white'
                              }`}
                              aria-hidden="true"
                            >
                              {isChecked && (
                                <svg className="h-3 w-3" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M3 8.5l3 3 7-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                              )}
                            </span>
                            <span className="text-sm">{project.name}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Provider Selection */}
                  <div>
                    <Label className="font-secondary uppercase text-xs tracking-wider text-slate-500">
                      Prestador/Fornecedor *
                    </Label>
                    <select
                      value={directFormData.provider_id || ''}
                      onChange={(e) => handleDirectProviderChange(e.target.value)}
                      className="mt-1 w-full h-10 px-3 rounded-sm border border-slate-200 bg-white text-sm"
                      data-testid="direct-provider-select"
                    >
                      <option value="">Selecione</option>
                      {providers.map(provider => (
                        <option key={provider.id} value={provider.id}>{provider.name}</option>
                      ))}
                    </select>
                  </div>

                  {/* Service Selection */}
                  {directFormData.provider_id && directFormData.selectedProjects.length > 0 && (
                    <div>
                      <Label className="font-secondary uppercase text-xs tracking-wider text-slate-500">
                        Serviço *
                      </Label>
                      {allServicesMemo.length > 0 ? (
                        <select
                          value={directFormData.service_code || ''}
                          onChange={(e) => handleDirectServiceChange(e.target.value)}
                          className="mt-1 w-full h-10 px-3 rounded-sm border border-slate-200 bg-white text-sm"
                          data-testid="direct-service-select"
                        >
                          <option value="">Selecione</option>
                          {allServicesMemo.map((service) => (
                            <option key={service.service_code} value={service.service_code}>
                              {service.service_code} - {service.service_description}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <p className="text-sm text-red-600 mt-1">
                          Nenhum serviço cadastrado nos centros de custo
                        </p>
                      )}
                    </div>
                  )}

                  {/* Date and Total Amount */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="font-secondary uppercase text-xs tracking-wider text-slate-500">
                        Data *
                      </Label>
                      <Input
                        type="date"
                        value={directFormData.launch_date}
                        onChange={(e) => setDirectFormData({ ...directFormData, launch_date: e.target.value })}
                        required
                        className="mt-1 rounded-sm"
                      />
                    </div>
                    <div>
                      <Label className="font-secondary uppercase text-xs tracking-wider text-slate-500">
                        Valor Total (R$) *
                      </Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={directFormData.total_amount}
                        onChange={(e) => setDirectFormData({ ...directFormData, total_amount: e.target.value })}
                        required
                        className="mt-1 rounded-sm"
                      />
                    </div>
                  </div>

                  {/* Rateio Table */}
                  {directFormData.selectedProjects.length > 0 && directFormData.total_amount && (
                    <div>
                      <Label className="font-secondary uppercase text-xs tracking-wider text-slate-500 mb-2 block">
                        Rateio por Projeto
                      </Label>
                      <div className="border rounded-sm overflow-hidden">
                        <table className="w-full text-sm">
                          <thead className="bg-slate-50">
                            <tr>
                              <th className="text-left p-2 font-medium">Projeto</th>
                              <th className="text-right p-2 font-medium w-32">Valor (R$)</th>
                            </tr>
                          </thead>
                          <tbody>
                            {directFormData.selectedProjects.map(proj => {
                              const project = projects.find(p => p.id === proj.project_id);
                              return (
                                <tr key={proj.project_id} className="border-t">
                                  <td className="p-2">{project?.name}</td>
                                  <td className="p-2">
                                    <Input
                                      type="number"
                                      step="0.01"
                                      value={proj.amount}
                                      onChange={(e) => updateProjectAmount(proj.project_id, e.target.value)}
                                      className="h-8 text-right"
                                      placeholder="0.00"
                                    />
                                  </td>
                                </tr>
                              );
                            })}
                            <tr className="border-t bg-slate-50 font-medium">
                              <td className="p-2">Total Rateio</td>
                              <td className={`p-2 text-right ${
                                Math.abs(calculateRateioTotal() - parseFloat(directFormData.total_amount || 0)) > 0.01
                                  ? 'text-red-600'
                                  : 'text-emerald-600'
                              }`}>
                                R$ {calculateRateioTotal().toFixed(2)}
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                      {Math.abs(calculateRateioTotal() - parseFloat(directFormData.total_amount || 0)) > 0.01 && (
                        <p className="text-xs text-red-600 mt-1">
                          A soma do rateio deve ser igual ao valor total
                        </p>
                      )}
                    </div>
                  )}

                  {/* Payment Info */}
                  <div>
                    <Label className="font-secondary uppercase text-xs tracking-wider text-slate-500">
                      Informações de Pagamento
                    </Label>
                    <Textarea
                      value={directFormData.payment_info}
                      onChange={(e) => setDirectFormData({ ...directFormData, payment_info: e.target.value })}
                      rows={2}
                      className="mt-1 rounded-sm"
                    />
                  </div>

                  {/* Warning: provider has no empreita in some selected projects */}
                  {providerNewInProjects.length > 0 && (
                    <div className="p-3 bg-amber-50 border border-amber-200 rounded-sm text-xs text-amber-800" data-testid="provider-new-warning">
                      <p className="font-medium">⚠️ Atenção: o prestador não tem empreita vinculada em:</p>
                      <ul className="list-disc list-inside mt-1">
                        {providerNewInProjects.map((name) => <li key={name}>{name}</li>)}
                      </ul>
                      <p className="mt-1">O lançamento pode ser criado mesmo assim, mas confirme se é intencional.</p>
                    </div>
                  )}

                  {/* Receipt Upload */}
                  <div>
                    <Label className="font-secondary uppercase text-xs tracking-wider text-slate-500">
                      Comprovante *
                    </Label>
                    <div className="mt-1">
                      <input
                        type="file"
                        ref={directFileInputRef}
                        onChange={(e) => setDirectFormData({ ...directFormData, receiptFile: e.target.files[0] })}
                        accept=".jpg,.jpeg,.png,.pdf"
                        className="hidden"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => directFileInputRef.current?.click()}
                        className="w-full"
                      >
                        <Upload className="h-4 w-4 mr-2" />
                        {directFormData.receiptFile ? directFormData.receiptFile.name : 'Selecionar Arquivo'}
                      </Button>
                    </div>
                  </div>

                  <div className="flex gap-2 justify-end pt-4 border-t">
                    <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                      Cancelar
                    </Button>
                    <Button type="submit" className="bg-purple-600 text-white hover:bg-purple-700">
                      Criar Lançamento Direto
                    </Button>
                  </div>
                </form>
              )}
            </DialogContent>
          </Dialog>
          </div>
        )}
      </div>

      {/* Pending launches for share mode (client only) */}
      {isShareMode && !isAdmin && pendingForShare.length > 0 && (
        <div data-testid="share-pending-launches">
          <h2 className="text-lg font-semibold text-slate-800 mb-3">Selecione um Lançamento Pendente</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {pendingForShare.map((launch) => {
              const project = projects.find(p => p.id === launch.project_id);
              return (
                <div key={launch.id}
                  className="bg-white border-2 border-blue-200 rounded-sm shadow-sm p-5 hover:border-blue-400 hover:shadow-md transition-all cursor-pointer"
                  onClick={() => handleSharedFileUpload(launch)}
                  data-testid="share-launch-card"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="p-2 rounded-sm bg-blue-50">
                      <Upload className="h-5 w-5 text-blue-600" />
                    </div>
                    {getStatusBadge(launch.status)}
                  </div>
                  <h3 className="font-semibold text-slate-900 mb-1">{launch.service_description}</h3>
                  <p className="text-xs text-slate-500 mb-2">Projeto: {project?.name || '-'}</p>
                  <div className="flex items-center justify-between mt-3 pt-3 border-t border-blue-100">
                    <p className="text-xs text-blue-600 font-medium">Toque para anexar comprovante</p>
                    <p className="font-semibold text-slate-900">R$ {launch.amount?.toFixed(2)}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {isShareMode && !isAdmin && pendingForShare.length === 0 && (
        <div className="text-center py-8 bg-white border border-blue-200 rounded-sm">
          <Receipt className="h-8 w-8 text-blue-300 mx-auto mb-2" />
          <p className="text-sm text-slate-500">Nenhum lançamento pendente para anexar comprovante</p>
        </div>
      )}

      {/* Client Launches Section */}
      <div>
        <h2 className="text-lg font-semibold text-slate-800 mb-3">
          {isAdmin ? 'Lançamentos (Cliente)' : 'Seus Lançamentos'}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {clientLaunches.map((launch) => {
            const project = projects.find(p => p.id === launch.project_id);
            return (
              <div
                key={launch.id}
                className="bg-white border border-slate-200 rounded-sm shadow-sm p-5 hover:border-slate-300 hover:shadow-md transition-all cursor-pointer"
                onClick={() => handleOpenDetails(launch.id)}
                data-testid="launch-card"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="p-2 rounded-sm bg-accent/10">
                    <Receipt className="h-5 w-5 text-accent" strokeWidth={1.5} />
                  </div>
                  {getStatusBadge(launch.status)}
                </div>
                <h3 className="font-semibold text-slate-900 mb-1">{launch.service_description}</h3>
                <p className="text-xs text-slate-500 mb-2">Projeto: {project?.name || '-'}</p>
                <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-100">
                  <p className="text-xs text-slate-500">
                    {new Date(launch.launch_date).toLocaleDateString('pt-BR')}
                  </p>
                  <p className="font-semibold text-slate-900">
                    R$ {launch.amount?.toFixed(2)}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
        {clientLaunches.length === 0 && (
          <div className="text-center py-8 bg-white border border-slate-200 rounded-sm">
            <Receipt className="h-8 w-8 text-slate-300 mx-auto mb-2" />
            <p className="text-sm text-slate-500">Nenhum lançamento encontrado</p>
          </div>
        )}
      </div>

      {/* Direct Launches Section - Admin Only */}
      {isAdmin && directLaunches.length > 0 && (
        <div>
          <h2 className="text-lg font-semibold text-slate-800 mb-3 flex items-center gap-2">
            Lançamentos Diretos
            <span className="text-xs font-normal text-slate-500">(não contabilizam no projeto)</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {directLaunches.map((launch) => {
              const project = projects.find(p => p.id === launch.project_id);
              return (
                <div
                  key={launch.id}
                  className="bg-white border border-purple-200 rounded-sm shadow-sm p-5 hover:border-purple-300 hover:shadow-md transition-all cursor-pointer"
                  onClick={() => handleOpenDetails(launch.id)}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="p-2 rounded-sm bg-purple-50">
                      <Receipt className="h-5 w-5 text-purple-600" strokeWidth={1.5} />
                    </div>
                    <div className="flex gap-1">
                      {getLaunchTypeBadge(launch.launch_type)}
                      {getStatusBadge(launch.status)}
                    </div>
                  </div>
                  <h3 className="font-semibold text-slate-900 mb-1">{launch.service_description}</h3>
                  <p className="text-xs text-slate-500 mb-2">Projeto: {project?.name || '-'}</p>
                  <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-100">
                    <p className="text-xs text-slate-500">
                      {new Date(launch.launch_date).toLocaleDateString('pt-BR')}
                    </p>
                    <p className="font-semibold text-slate-900">
                      R$ {launch.amount?.toFixed(2)}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Launch Detail Dialog */}
      <Dialog open={isDetailDialogOpen} onOpenChange={setIsDetailDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-secondary uppercase tracking-tight">
              Detalhes do Lançamento
            </DialogTitle>
          </DialogHeader>
          
          {selectedLaunch && (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-sm text-slate-600">Status:</span>
                {getStatusBadge(selectedLaunch.status)}
              </div>
              
              {selectedLaunch.launch_type === 'direct' && (
                <div className="flex justify-between items-center">
                  <span className="text-sm text-slate-600">Tipo:</span>
                  {getLaunchTypeBadge(selectedLaunch.launch_type)}
                </div>
              )}
              
              <div>
                <span className="text-sm text-slate-600">Serviço:</span>
                <p className="font-medium">{selectedLaunch.service_description}</p>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <span className="text-sm text-slate-600">Valor:</span>
                  <p className="font-semibold text-lg">R$ {selectedLaunch.amount?.toFixed(2)}</p>
                </div>
                <div>
                  <span className="text-sm text-slate-600">Data:</span>
                  <p className="font-medium">
                    {new Date(selectedLaunch.launch_date).toLocaleDateString('pt-BR')}
                  </p>
                </div>
              </div>

              {selectedLaunch.payment_info && (
                <div>
                  <span className="text-sm text-slate-600">Informações de Pagamento:</span>
                  <div className="bg-slate-50 p-3 rounded-sm mt-1 relative">
                    <pre className="text-sm whitespace-pre-wrap font-primary">
                      {selectedLaunch.payment_info}
                    </pre>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="absolute top-1 right-1"
                      onClick={() => copyToClipboard(selectedLaunch.payment_info)}
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}

              {/* Receipt Section */}
              <div className="border-t pt-4">
                <span className="text-sm text-slate-600">Comprovante:</span>
                {selectedLaunch.receipt_file ? (
                  <div className="mt-2">
                    <Button
                      variant="outline"
                      className="w-full"
                      onClick={() => openReceipt(selectedLaunch.receipt_file)}
                    >
                      <FileText className="h-4 w-4 mr-2" />
                      Ver Comprovante
                    </Button>
                  </div>
                ) : (
                  <p className="text-sm text-slate-500 mt-1">Nenhum comprovante enviado</p>
                )}

                {/* Upload Receipt Button (for pending client launches) */}
                {(selectedLaunch.status === 'pending' || selectedLaunch.status === 'submitted_receipt') && selectedLaunch.launch_type !== 'direct' && (
                  <div className="mt-3">
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleUploadReceipt}
                      accept=".jpg,.jpeg,.png,.pdf"
                      className="hidden"
                    />
                    <Button
                      variant="outline"
                      className="w-full"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <Upload className="h-4 w-4 mr-2" />
                      {selectedLaunch.receipt_file ? 'Substituir Comprovante' : 'Enviar Comprovante'}
                    </Button>
                  </div>
                )}

                {/* Confirm Payment Button (separate from upload) */}
                {(selectedLaunch.status === 'pending' || selectedLaunch.status === 'submitted_receipt') && selectedLaunch.receipt_file && selectedLaunch.launch_type !== 'direct' && (
                  <div className="mt-3">
                    <Button
                      className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
                      onClick={handleConfirmPayment}
                    >
                      <CheckCircle2 className="h-4 w-4 mr-2" />
                      Confirmar Pagamento
                    </Button>
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FinancialLaunches;
